import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Card, Drawer, Form, Input, InputNumber, message,
  Popconfirm, Select, Space, Switch, Table, Tag,
} from "antd";
import { useState } from "react";
import {
  createGuideField, deleteGuideField, listGuideFieldsAdmin, updateGuideField,
  type GuideFieldIn, type GuideFieldOut,
} from "./api";

const TYPE_OPTS = [
  { value: "text", label: "单行文本" },
  { value: "textarea", label: "多行文本" },
  { value: "date", label: "日期" },
  { value: "select", label: "下拉选择" },
  { value: "number", label: "数字" },
];
const STAGE_OPTS = [
  { value: "Register", label: "注册阶段" },
  { value: "Sharing", label: "共享阶段" },
];

/** options 编辑器:一行一个选项,格式 value 或 value|中文名|英文名,与 JSONB [{value,label,label_en}] 互转 */
function optionsToText(options: GuideFieldOut["options"]): string {
  return (options ?? [])
    .map((o) => [o.value, o.label, o.label_en].filter(Boolean).join("|"))
    .join("\n");
}
function textToOptions(text: string | undefined): GuideFieldIn["options"] {
  const rows = (text ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [value, label, label_en] = line.split("|").map((s) => s.trim());
      return { value, label: label || null, label_en: label_en || null };
    });
  return rows.length ? rows : null;
}

export function GuideFieldsPage() {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();
  const [openCreate, setOpenCreate] = useState(false);
  const [editOne, setEditOne] = useState<GuideFieldOut | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const listQ = useQuery({ queryKey: ["guide-fields-admin"], queryFn: listGuideFieldsAdmin });
  const reload = () => qc.invalidateQueries({ queryKey: ["guide-fields-admin"] });

  const createM = useMutation({
    mutationFn: createGuideField,
    onSuccess: () => { msgApi.success("已创建"); setOpenCreate(false); createForm.resetFields(); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Omit<GuideFieldIn, "field">> }) =>
      updateGuideField(id, body),
    onSuccess: () => { msgApi.success("已更新"); setEditOne(null); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const delM = useMutation({
    mutationFn: deleteGuideField,
    onSuccess: () => { msgApi.success("已删除"); reload(); },
  });

  // 表单提交:options 文本 ↔ 结构互转在此收口;剥离编辑器辅助字段
  const toBody = (v: Record<string, unknown>): Record<string, unknown> => {
    const { options_text: optionsText, ...rest } = v;
    return { ...rest, options: textToOptions(optionsText as string | undefined) };
  };

  const formLayout = (isEdit: boolean) => (
    <>
      <Form.Item name="field" label="字段 key(创建后不可改)"
        rules={[{ required: true, pattern: /^[a-z][a-z0-9_]*$/, message: "小写字母/数字/下划线,字母开头" }]}>
        <Input disabled={isEdit} placeholder="如 registration_purpose" />
      </Form.Item>
      <Form.Item name="label" label="中文名称" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="label_en" label="英文名称">
        <Input />
      </Form.Item>
      <Form.Item name="group_name" label="分组名(同组归入一个卡片)">
        <Input />
      </Form.Item>
      <Space size={12} wrap>
        <Form.Item name="field_type" label="类型" initialValue="text" rules={[{ required: true }]}>
          <Select options={TYPE_OPTS} style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="stage" label="适用阶段" initialValue="Register" rules={[{ required: true }]}>
          <Select options={STAGE_OPTS} style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="order_num" label="排序(升序)" initialValue={0}>
          <InputNumber min={0} />
        </Form.Item>
      </Space>
      <Form.Item noStyle shouldUpdate={(a, b) => a.field_type !== b.field_type}>
        {({ getFieldValue }) =>
          getFieldValue("field_type") === "select" && (
            <Form.Item name="options_text" label="选项(一行一个:value 或 value|中文名|英文名)">
              <Input.TextArea rows={3} placeholder={"social_work|社会工作|Social Work"} />
            </Form.Item>
          )
        }
      </Form.Item>
      <Form.Item name="default_val" label="默认值">
        <Input />
      </Form.Item>
      <Space size={24}>
        <Form.Item name="required" label="必填" valuePropName="checked" initialValue={false}>
          <Switch />
        </Form.Item>
        <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
      </Space>
    </>
  );

  return (
    <div>
      {msgCtx}
      <Card
        title="动态字段配置"
        extra={
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => { createForm.resetFields(); setOpenCreate(true); }}>
            新增字段
          </Button>
        }
      >
        <Table<GuideFieldOut>
          rowKey="id"
          size="small"
          loading={listQ.isLoading}
          dataSource={listQ.data ?? []}
          pagination={false}
          columns={[
            { title: "字段 key", dataIndex: "field" },
            { title: "名称", dataIndex: "label", render: (v: string, r: GuideFieldOut) => (
              <Space size={4}>
                <span>{v}</span>
                {r.label_en ? <span style={{ color: "#999" }}>{`(${r.label_en})`}</span> : null}
              </Space>
            ) },
            { title: "分组", dataIndex: "group_name", render: (v) => v ?? "—" },
            { title: "类型", dataIndex: "field_type", render: (v) => <Tag>{v}</Tag> },
            { title: "阶段", dataIndex: "stage", render: (v) => (
              <Tag color={v === "Register" ? "blue" : "purple"}>{v}</Tag>
            ) },
            { title: "必填", dataIndex: "required", render: (v) => (v ? "是" : "否") },
            { title: "排序", dataIndex: "order_num", width: 70 },
            { title: "状态", dataIndex: "is_active", render: (v) => (
              v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>
            ) },
            { title: "操作", key: "ops", width: 110, render: (_, r) => (
              <Space>
                <Button size="small" type="text" icon={<EditOutlined />}
                  onClick={() => {
                    editForm.setFieldsValue({ ...r, options_text: optionsToText(r.options) });
                    setEditOne(r);
                  }}
                />
                <Popconfirm title="删除该字段?" onConfirm={() => delM.mutate(r.id)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ) },
          ]}
        />
      </Card>

      <Drawer open={openCreate} title="新增字段" width={520} onClose={() => setOpenCreate(false)}>
        <Form form={createForm} layout="vertical"
          onFinish={(v) => createM.mutate(toBody(v) as GuideFieldIn)}>
          {formLayout(false)}
          <Button type="primary" htmlType="submit" block loading={createM.isPending}>创建</Button>
        </Form>
      </Drawer>

      <Drawer open={!!editOne} title={`编辑字段: ${editOne?.field ?? ""}`} width={520}
        onClose={() => setEditOne(null)}>
        <Form form={editForm} layout="vertical"
          onFinish={(v) => {
            if (!editOne) return;
            const { field: _ignored, ...rest } = toBody(v); // field key 不可改,不下发
            updateM.mutate({ id: editOne.id, body: rest as Partial<Omit<GuideFieldIn, "field">> });
          }}>
          {formLayout(true)}
          <Space>
            <Button type="primary" htmlType="submit" loading={updateM.isPending}>保存</Button>
            <Button onClick={() => setEditOne(null)}>取消</Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
}
